import { ref } from 'vue'
import { FirebaseApp } from 'firebase/app'
import { AuthServices } from '@tritt/firebase-client'
import { useFirebaseAuth, isLoggedIn, user, authStateInitialized } from './auth.service'
import { initUserOrgs } from '@/tenants/org.service'

/**
 * BootstrapService orchestrates the application startup sequence.
 * 
 * Responsibilities:
 * 1. Initialize authentication
 * 2. Wait for auth state to be determined
 * 3. If authenticated, load user organizations
 * 4. Signal when the app is ready for interaction
 */
class BootstrapService {
    /** Reactive state indicating if the app has finished its startup sequence */
    public readonly isReady = ref(false)

    /** Promise that resolves when initialization is complete */
    public readonly initializationPromise: Promise<void>

    private resolveInit!: () => void
    private rejectInit!: (error: Error) => void

    constructor() {
        this.initializationPromise = new Promise((resolve, reject) => {
            this.resolveInit = resolve
            this.rejectInit = reject
        })
    }

    /**
     * Initialize the application.
     * Called by main.ts during app startup.
     * 
     * @param app - Firebase app instance
     * @param authServices - Tritt auth services
     */
    async init(app: FirebaseApp, authServices: AuthServices): Promise<void> {
        console.log('[BootstrapService] Starting initialization...')

        try {
            // Step 1: Initialize auth
            const { init: authInit } = useFirebaseAuth()
            await authInit(app, authServices)

            // Step 2: Wait for auth state to settle
            await authStateInitialized
            console.log('[BootstrapService] Auth initialized, isLoggedIn:', isLoggedIn.value)

            // Step 3: If logged in, load organizations
            if (isLoggedIn.value && user.value?.uid) {
                console.log('[BootstrapService] Loading organizations for user:', user.value.uid)
                await initUserOrgs(user.value.uid)
                console.log('[BootstrapService] Organizations loaded')
            }

            // Step 4: Mark as ready
            this.isReady.value = true
            this.resolveInit()
            console.log('[BootstrapService] Initialization complete')

        } catch (error) {
            console.error('[BootstrapService] Initialization failed:', error)
            this.rejectInit(error instanceof Error ? error : new Error(String(error)))
            throw error
        }
    }
}

/** Singleton instance */
export const bootstrapService = new BootstrapService()
